import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/cliente';
import type {
  BorradorPrefactura, ClaveSat, Cliente, ConceptoPrefactura,
  DatosDeEmision, DatosFiscalesDelCliente, Pagina, Prefactura, TipoFactura,
  VistaPreviaPrefactura,
} from '../api/tipos';
import { Cargando, Dialogo, Error, Faltantes } from '../componentes/comunes';
import { ComprobanteDeLaFactura, conNombre, dia, cifra } from '../componentes/ComprobanteDeLaFactura';
import { folioOT } from '../folio';

/**
 * Crear prefactura.
 *
 * APRISA no timbra: de aquí sale el registro de lo que se va a cobrar y
 * el archivo TXT que Master EDI levanta para generar el XML y el PDF con
 * el sello del SAT. Por eso la pantalla dice prefactura en todas partes;
 * factura será cuando vuelva el UUID.
 *
 * **La pantalla va en el orden del papel.** Comprobante, emisor, cliente,
 * condiciones de pago, referencias, entrega, conceptos, totales: lo mismo
 * que se lee de arriba abajo en la factura impresa. No es decoración.
 * Quien factura tiene el PDF de la vez pasada al lado y va comparando; si
 * la pantalla ordena los campos de otro modo, comparar obliga a buscar
 * cada dato, y ahí es donde se salta uno.
 *
 * **Casi nada se teclea.** El emisor siempre es APRISA y se precarga
 * entero. Los datos fiscales del cliente salen de su ficha en cuanto se
 * escoge —RFC, régimen, domicilio, condiciones de pago—. Y las claves del
 * SAT se escogen de una lista, no se recuerdan de memoria: una clave del
 * SAT tecleada mal no rompe nada visible, sale timbrada.
 *
 * **Lo prellenado se puede corregir.** Los datos fiscales se proponen con
 * los del cliente y se pueden cambiar aquí; cambiarlos cambia esta
 * factura y nada más. Para cambiarlos siempre está el catálogo de
 * Clientes, y la pantalla lo dice donde toca.
 *
 * Los tres tipos se llenan distinto y por eso el formulario cambia:
 *
 *   iguala      un concepto de servicio y el periodo que cubre. El precio
 *               sale de la tarifa pactada —tipo de iguala por formato de
 *               tienda—; si no hay tarifa capturada, se teclea y la
 *               pantalla dice por qué faltó.
 *   correctivo  varios conceptos con el precio que validó Operaciones.
 *   libre       facturación captura todo, sin orden detrás.
 *
 * Los totales se calculan aquí sólo para que se vean mientras se captura.
 * El que vale es el que calcula la API al guardar, con la misma función
 * que escribe el archivo: si la pantalla fuera la que manda, la
 * prefactura podría decir un total y el TXT otro.
 */

function moneda(valor: number, divisa = 'MXN'): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: divisa });
}

/**
 * La fecha como la espera un campo de tipo date.
 *
 * Se arma con las partes locales y no con toISOString, que pasa a UTC y
 * en México devuelve el día anterior toda la tarde.
 */
function comoCampoDeFecha(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** El importe de un renglón, redondeado como lo redondea la API. */
function importeDe(cantidad: number, precio: number): number {
  return Math.round(cantidad * precio * 100) / 100;
}

/** Lo que se manda: vacío significa «lo que diga el cliente». */
function oNada(valor: string): string | undefined {
  return valor.trim() || undefined;
}

interface Renglon extends ConceptoPrefactura {
  /** Sólo para la lista de React: el catálogo no da identidad al renglón. */
  llave: number;
}

let contador = 0;
function nuevoRenglon(base: Partial<ConceptoPrefactura> = {}): Renglon {
  contador += 1;
  return {
    llave: contador,
    descripcion: '',
    cantidad: 1,
    precioUnitario: 0,
    claveProdServ: '73152101',
    claveUnidad: UNIDAD_SAT,
    unidad: 'PZA',
    emiteCodigoUpc: true,
    objetoImpuesto: '02',
    tasaIva: TASA_IVA,
    ...base,
  };
}

/**
 * Los datos fiscales que la prefactura copia y aquí se pueden corregir.
 *
 * Se llevan aparte del resto del formulario porque comparten origen: los
 * seis salen del cliente y los seis se pueden cambiar sin tocarlo.
 */
interface Fiscales {
  usoCfdi: string;
  formaPago: string;
  formaPagoTexto: string;
  metodoPago: string;
  metodoPagoEdi: string;
  condicionesPago: string;
  cuentaPago: string;
  diasCredito: string;
}

/**
 * La moneda no es un campo: APRISA factura en pesos.
 *
 * Se manda explícita y no se deja al valor del cliente porque lo que se
 * guarda tiene que ser lo que la pantalla enseña. Un cliente con otra
 * moneda en su ficha facturaría en esa moneda sin que nadie lo viera.
 */
const MONEDA = 'MXN';

/**
 * La clave de unidad del SAT, que no cambia.
 *
 * E48 es «unidad de servicio», y es la que llevan los tres tipos de
 * factura en las cuatro timbradas de muestra —las de mano de obra y las
 * de pieza—. Lo que cambia es cómo se imprime: PZA o SERVICIO.
 *
 * Karin: «clave unidad es E48 fijo, unidad es pieza o servicio».
 */
const UNIDAD_SAT = 'E48';

/**
 * El IVA que se traslada, también fijo.
 *
 * 16% en todos los renglones de todas las facturas de muestra. Se deja
 * de capturar por renglón: una columna con el mismo número en cada fila
 * es ruido, y la única manera de usarla era equivocarse.
 *
 * Si algún día hay un renglón exento o a otra tasa, el campo vuelve.
 * Mientras tanto el valor viaja igual en cada concepto, porque el
 * archivo lo pide renglón por renglón.
 */
const TASA_IVA = 16;

/**
 * Lo que una factura de Walmart trae siempre igual.
 *
 * Karin, repasando la pantalla: «los datos ya son fijos, es muy raro que
 * cambien». Y es cierto: las cuatro facturas timbradas de muestra dicen
 * exactamente esto, factura tras factura.
 *
 * Que sean fijos cambia qué pinta la pantalla, no qué guarda la base: se
 * siguen copiando a la prefactura como siempre, porque una factura de
 * hace un año tiene que poder decir las condiciones de entonces aunque
 * hoy sean otras. Lo que se quita es el desplegable, que sólo servía
 * para escoger mal.
 *
 * `diasCredito` es el único que se propone y se puede cambiar: Karin
 * pidió 90 y las facturas de agosto dicen 30, así que el plazo se
 * renegocia y la pantalla tiene que dejarlo.
 */
const DE_WALMART = {
  serie: 'E',
  usoCfdi: 'G03',
  metodoPago: 'PPD',
  /** El METPAG del archivo, que no es del catálogo del SAT. */
  metodoPagoEdi: '03',
  formaPago: '99',
  formaPagoTexto: 'Por Definir',
  condicionesPago: 'CREDITO',
  diasCredito: '90',
} as const;

function fiscalesDe(c: DatosFiscalesDelCliente | Cliente | null): Fiscales {
  return {
    usoCfdi: c?.usoCfdi ?? '',
    formaPago: c?.formaPago ?? '',
    formaPagoTexto: c?.formaPagoTexto ?? '',
    metodoPago: c?.metodoPago ?? '',
    metodoPagoEdi: c?.metodoPagoEdi ?? '',
    condicionesPago: c?.condicionesPago ?? '',
    cuentaPago: '',
    diasCredito: c?.diasCredito === null || c?.diasCredito === undefined
      ? '' : String(c.diasCredito),
  };
}

/**
 * Los mismos, ya puestos, para una factura que viene de una orden.
 *
 * Se aplican encima de lo que diga la ficha del cliente y no al revés: si
 * alguien capturó otra forma de pago en Clientes, la factura de una OT
 * sigue saliendo como tiene que salir. La ficha manda en la factura
 * libre, donde el cliente puede ser otro.
 */
function fiscalesDeUnaOrden(c: DatosFiscalesDelCliente | Cliente | null): Fiscales {
  return {
    ...fiscalesDe(c),
    usoCfdi: DE_WALMART.usoCfdi,
    formaPago: DE_WALMART.formaPago,
    formaPagoTexto: DE_WALMART.formaPagoTexto,
    metodoPago: DE_WALMART.metodoPago,
    metodoPagoEdi: DE_WALMART.metodoPagoEdi,
    condicionesPago: DE_WALMART.condicionesPago,
    cuentaPago: '',
    /**
     * Los 90, siempre, y no lo que diga la ficha del cliente.
     *
     * Karin: «los días de crédito sí ponles 90 por default cuando viene
     * de orden». Antes ganaba la ficha si tenía algo capturado, y ahí
     * están los 30 viejos: la factura habría salido a 30 días sin que
     * nadie lo notara, y el vencimiento que usa Cobranza con ella.
     *
     * El campo se sigue pudiendo cambiar. Es lo único de este bloque que
     * se captura, porque el plazo se renegocia y las facturas de agosto
     * todavía dicen 30.
     */
    diasCredito: DE_WALMART.diasCredito,
  };
}

/** Una factura que ya lleva el mismo número de orden de trabajo. */
interface FacturaConEsaOrden {
  id: string;
  folio: string;
  fecha: string;
  total: number;
  cancelada: boolean;
  timbrada: boolean;
}

export default function CrearPrefactura({
  ordenTrabajoId, alCrear, alCancelar,
}: {
  /** La orden que se va a facturar. Sin ella, la prefactura es libre. */
  ordenTrabajoId?: string | null;
  alCrear: (prefactura: Prefactura) => void;
  alCancelar?: () => void;
}) {
  const [emision, setEmision] = useState<DatosDeEmision | null>(null);
  const [borrador, setBorrador] = useState<BorradorPrefactura | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  /** El comprobante como va a quedar. Nulo mientras no se pida. */
  const [previa, setPrevia] = useState<VistaPreviaPrefactura | null>(null);
  const [previsualizando, setPrevisualizando] = useState(false);

  const [tipo, setTipo] = useState<TipoFactura>('libre');
  const [serie, setSerie] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [sucursalId, setSucursalId] = useState<string | null>(null);
  const [fiscales, setFiscales] = useState<Fiscales>(fiscalesDe(null));

  const [poNumero, setPoNumero] = useState('');
  const [fechaOc, setFechaOc] = useState('');
  const [contraRecibo, setContraRecibo] = useState('');
  /**
   * Las facturas que ya llevan esta orden de trabajo.
   *
   * Karin: «en orden de trabajo poner una validacion que si ponen una
   * repetida les notifique que ya se habia hecho una con ese numero».
   * Avisa, no impide: hay razones legítimas —una refacturación, un trabajo
   * que el cliente partió en dos— y bloquearlo obligaría a inventar cómo
   * saltarse el bloqueo. Lo que evita es teclear la OT de la factura
   * anterior por costumbre.
   */
  const [yaFacturada, setYaFacturada] = useState<FacturaConEsaOrden[]>([]);
  const [contraReciboFecha, setContraReciboFecha] = useState('');
  /*
      Número de cliente y número de agente se quitaron de la pantalla.

      Karin: «número de cliente y número de agente quitar». En las cuatro
      facturas timbradas de muestra los dos van vacíos, así que eran dos
      campos que nadie llenaba y que quien factura tenía que leer para
      descubrir que no le tocaban. Las columnas se quedan en la base —hay
      facturas viejas con algo capturado— y el DTO los sigue admitiendo
      por si algún cliente los pide.
  */

  const [eanEntrega, setEanEntrega] = useState('');
  /*
      Observaciones se quitó de la pantalla.

      La columna y la etiqueta OBSERG del archivo se quedan: hay facturas
      viejas con algo escrito, y la de la iguala de muestra imprime
      «PEDIDO:  RECIBO:» bajo el primer concepto. Lo que se retira es el
      campo, porque en las facturas de Walmart que se emiten hoy va
      vacío y era un renglón más que leer para descubrir que no tocaba.

      Si algún cliente vuelve a pedir una leyenda en el cuerpo del
      comprobante, el campo regresa: nada en la base ni en el generador
      del TXT se tocó.
  */
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFin, setPeriodoFin] = useState('');
  const [renglones, setRenglones] = useState<Renglon[]>([nuevoRenglon()]);
  /**
   * Si los conceptos vienen de una orden.
   *
   * Lo dice la API, no la pantalla: es la misma que después se niega a
   * guardar unos conceptos que no sean los de la orden, y deducirlo aquí
   * por separado sería tener la regla en dos sitios —o sea, en ninguno—.
   */
  const [vienenDeLaOrden, setVienenDeLaOrden] = useState(false);
  /**
   * Si esta factura sale de una orden de trabajo.
   *
   * Hoy eso quiere decir Walmart, y con Walmart casi todo está pactado:
   * serie, uso de CFDI, método y forma de pago, condiciones. Karin: «los
   * datos ya son fijos, es muy raro que cambien».
   *
   * Lo que cambia es la pantalla, no lo que se guarda: la prefactura
   * sigue copiando cada valor, porque una factura de hace un año tiene
   * que poder decir las condiciones de entonces. Lo que se quita es el
   * desplegable, que sólo servía para escoger mal.
   *
   * La factura libre conserva todos los campos: ahí el cliente puede ser
   * otro y nada está dado.
   */
  const deLaOrden = !!ordenTrabajoId;
  const [clientes, setClientes] = useState<Cliente[]>([]);

  const sat = emision?.sat;
  const escogido = clientes.find((c) => c.id === clienteId) ?? null;
  // De la orden o escogido a mano: para todo lo que sigue es lo mismo.
  const receptor: DatosFiscalesDelCliente | null = borrador?.cliente ?? escogido;

  // Emisor, series y claves del SAT: nada de esto cambia mientras se
  // captura, así que se pide una vez y en una sola vuelta.
  useEffect(() => {
    api.get<DatosDeEmision>('/facturacion/prefacturas/emision')
      .then((e) => {
        setEmision(e);

        /**
         * La serie arranca en la E.
         *
         * Karin: «te falta poner en comprobante la serie E como default».
         * Y tenía razón: esto cogía «la primera activa», que es la primera
         * por orden alfabético y no la que se usa. Con la E activa salía
         * la E de casualidad, y en cuanto hubiera otra activa antes —una
         * A, una B— la pantalla arrancaría en la que no es.
         *
         * Es la de las facturas de Walmart, que es casi todo lo que se
         * emite, y la misma que ya se fija sola cuando la factura viene de
         * una orden. Aquí era el único sitio donde se dejaba al azar.
         *
         * La activa sigue siendo el respaldo, por si algún día la E se
         * retira: entonces lo correcto es ofrecer la que esté viva, no
         * arrancar vacío.
         */
        const laE = e.series.find((x) => x.serie === DE_WALMART.serie && x.activa);
        const cualquieraActiva = e.series.find((x) => x.activa);

        setSerie((actual) => actual
          || laE?.serie
          || cualquieraActiva?.serie
          || '');
      })
      .catch(setError);
  }, []);

  // Sólo hacen falta cuando no hay orden detrás: en las demás, el cliente
  // lo pone la orden y elegir otro sería facturarle a quien no fue.
  useEffect(() => {
    if (ordenTrabajoId) return;

    api.get<Pagina<Cliente>>('/clientes', { limite: 200, estado: 'activo' })
      .then((r) => setClientes(r.data))
      .catch(() => undefined);
  }, [ordenTrabajoId]);

  /**
   * El contra-recibo arranca hoy. En todas, no sólo en las libres.
   *
   * Karin: «en "Contra-Recibo y fecha" habíamos mencionado que la fecha a
   * registrar sería "Hoy", pero aparece la fecha en la que se cerró la
   * orden».
   *
   * Yo lo había puesto sólo en las libres, y la que venía de una orden
   * tomaba su fecha de cierre. Estaba mal, y el error era de concepto: el
   * contra-recibo no es el día en que se terminó el trabajo, es el día en
   * que se le entrega la factura al cliente y él la acusa. Una orden
   * cerrada el 5 y facturada el 13 tiene contra-recibo del 13, y con la
   * fecha de cierre el cliente recibía un documento fechado ocho días
   * antes de existir.
   *
   * Sólo si está vacío, para no pisar lo que alguien acabe de elegir al
   * volver a pintar la pantalla.
   *
   * Se arma con las partes locales y no con `toISOString`, que da la fecha
   * en UTC: a las nueve de la noche en México eso ya es mañana, y el campo
   * arrancaría con el día siguiente.
   */
  useEffect(() => {
    setContraReciboFecha((actual) => {
      if (actual) return actual;

      const hoy = new Date();
      const mes = String(hoy.getMonth() + 1).padStart(2, '0');
      const dia = String(hoy.getDate()).padStart(2, '0');
      return `${hoy.getFullYear()}-${mes}-${dia}`;
    });
  }, []);


  // Al cambiar de cliente se reproponen sus condiciones. Lo que se haya
  // corregido a mano se pierde, y está bien: eran las del cliente
  // anterior, y dejarlas puestas es facturar con las condiciones de otro.
  useEffect(() => {
    if (!receptor) return;

    // Con orden detrás es una factura de Walmart, y ahí las condiciones
    // están dadas: la ficha sólo aporta los días de crédito pactados.
    setFiscales(ordenTrabajoId ? fiscalesDeUnaOrden(receptor) : fiscalesDe(receptor));
  }, [receptor?.id, ordenTrabajoId]);

  // La propuesta de la orden. Se pide a la API y no se compone aquí para
  // que dos versiones del frontend no puedan facturar distinto.
  useEffect(() => {
    if (!ordenTrabajoId) { setCargando(false); return; }

    setCargando(true);
    api.get<BorradorPrefactura>('/facturacion/prefacturas/borrador', { ordenTrabajoId })
      .then((b) => {
        setBorrador(b);
        setTipo(b.tipo);
        setClienteId(b.cliente.id);
        setSucursalId(b.sucursal?.id ?? null);
        setPoNumero(b.poNumero);
        setEanEntrega(b.eanEntrega ?? '');
        /**
         * El importe se prellena cuando el sistema ya lo sabe.
         *
         * La iguala lo sabe por su tarifa pactada, y la preventiva por la
         * suma de lo que se hizo en la visita. Los dos salían en un aviso
         * al lado y el renglón se quedaba en cero, así que había que
         * teclear a mano un número que el sistema tenía calculado —y
         * teclear un importe que ya existe es la manera más tonta de
         * facturar de menos—.
         *
         * Sólo cuando hay un renglón: el correctivo propone cuatro y
         * repartir la suma entre ellos es una decisión que no me toca
         * —está sin contestar en la lista de APRISA— y meterla aquí sería
         * inventarla.
         *
         * Y sólo sobre el cero: si el catálogo trae un precio sugerido,
         * ése manda, que para eso está.
         */
        const propuesto = b.tipo === 'preventivo'
          ? b.precioValidado
          : b.tipo === 'iguala' ? b.tarifa?.precio ?? null : null;

        // Si la factura viene de una OT, sus renglones son los de la
        // orden y aquí no se capturan: se leen.
        setVienenDeLaOrden(b.conceptosVienenDeLaOrden === true);
        setRenglones(b.conceptos.map((c) => nuevoRenglon(
          b.conceptos.length === 1 && propuesto !== null
            && !(Number(c.precioUnitario) > 0)
            ? { ...c, precioUnitario: propuesto }
            : c,
        )));

        /**
         * Aquí ya no se toca el contra-recibo: lo pone en hoy el efecto de
         * arriba, y la fecha de cierre de la orden dejó de proponerse.
         *
         * Era lo que hacía antes, y Karin lo cachó: el contra-recibo se
         * fecha el día en que se entrega la factura, no el día en que se
         * terminó el trabajo. Entre uno y otro pueden pasar días.
         */

        /**
         * Y la de la orden de compra es la del alta de la OT.
         *
         * Karin: «fecha de orden (toma la fecha que se dio de alta la
         * orden en sistema)». Se guarda, aunque el formato impreso ya no
         * la enseñe: el archivo de Master EDI no lleva campo para ella, y
         * dibujarla en la vista previa prometía un dato que la factura no
         * iba a traer.
         */
        if (b.orden.fechaAlta) {
          setFechaOc(comoCampoDeFecha(new Date(b.orden.fechaAlta)));
        }

        // La serie de una factura de Walmart es la E. Se fija aquí para
        // que lo que se enseña y lo que se manda sean lo mismo.
        setSerie(DE_WALMART.serie);

        // Con tarifa pactada, el periodo se propone completo: el mes en
        // curso para la mensual, el trimestre para la trimestral. Es lo
        // que se factura casi siempre, y corregirlo cuesta dos clics.
        if (b.tarifa) {
          const meses = { mensual: 1, bimestral: 2, trimestral: 3 }[b.tarifa.periodicidad] ?? 1;
          const hoy = new Date();
          const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
          const fin = new Date(hoy.getFullYear(), hoy.getMonth() + meses, 0);
          setPeriodoInicio(comoCampoDeFecha(inicio));
          setPeriodoFin(comoCampoDeFecha(fin));
        }
      })
      .catch(setError)
      .finally(() => setCargando(false));
  }, [ordenTrabajoId]);

  /**
   * Si esa orden de trabajo ya se facturó, para poder avisarlo.
   *
   * Va aquí, con los demás efectos y **antes de cualquier `return`**. La
   * primera versión quedó debajo del `return` de «Preparando la
   * prefactura…», o sea que el hook sólo corría cuando la pantalla ya
   * había cargado: React cuenta los hooks por orden y espera los mismos en
   * cada pintada, así que en cuanto cargaba encontraba dos de más y se
   * negaba a seguir —pantalla en blanco—. Y encima estaba escrito dos
   * veces.
   *
   * Se pregunta al dejar de teclear, no en cada letra: trescientos
   * milisegundos, lo mismo que el buscador de sucursales. Con un folio de
   * diez dígitos, preguntar por letra son diez consultas por un número. Y
   * a partir de tres caracteres, porque con uno o dos cualquier factura
   * coincidiría y el aviso saldría siempre.
   */
  useEffect(() => {
    const buscado = poNumero.trim();
    if (buscado.length < 3) { setYaFacturada([]); return undefined; }

    const t = setTimeout(() => {
      api.get<{ facturas: FacturaConEsaOrden[] }>(
        '/facturacion/prefacturas/po-usado',
        { poNumero: buscado },
      )
        .then((r) => setYaFacturada(r.facturas))
        // En silencio: es un aviso, no una validación. Que no se pueda
        // comprobar no es razón para estorbarle a quien factura.
        .catch(() => setYaFacturada([]));
    }, 300);

    return () => clearTimeout(t);
  }, [poNumero]);

  // Lo cotizado en la orden, para compararlo con lo que se va a facturar.
  // Un peso de diferencia casi siempre es redondeo; se avisa desde uno,
  // porque quien factura sabe distinguir y quien no lo ve, no.
  const totalCotizado = useMemo(
    () => Math.round((borrador?.cotizacion ?? [])
      .reduce((suma, r) => suma + r.precioVenta, 0) * 100) / 100,
    [borrador],
  );

  const totales = useMemo(() => {
    let subtotal = 0;
    let iva = 0;

    for (const r of renglones) {
      const importe = importeDe(Number(r.cantidad) || 0, Number(r.precioUnitario) || 0);
      subtotal += importe;
      iva += Math.round(importe * ((Number(r.tasaIva) || 0) / 100) * 100) / 100;
    }

    const redondear = (n: number) => Math.round(n * 100) / 100;
    return { subtotal: redondear(subtotal), iva: redondear(iva), total: redondear(subtotal + iva) };
  }, [renglones]);

  const difiere = totalCotizado > 0 && Math.abs(totales.subtotal - totalCotizado) >= 1;

  function cambiar(llave: number, campo: keyof ConceptoPrefactura, valor: unknown) {
    setRenglones((rs) => rs.map((r) => (r.llave === llave ? { ...r, [campo]: valor } : r)));
  }

  /* El armado de un renglón desde el catálogo de conceptos vivía aquí.
     Se retiró junto con su desplegable —Karin: «no lo vamos a ocupar
     por el momento»— porque el catálogo todavía no tiene pantalla donde
     mantenerse (tarea #193). Está en el historial, en el commit que
     quitó el select, para cuando haya qué ofrecer. */

  const marcasPendientes = renglones.some((r) => /\{(sucursal|determinante)\}/.test(r.descripcion));
  const sinDescripcion = renglones.some((r) => !r.descripcion.trim());
  const sinPrecio = renglones.some((r) => !(Number(r.precioUnitario) > 0));

  /**
   * Cómo se marca un campo que falta.
   *
   * Karin: «indicame en rojo lo que falta, resaltalo para que sepa
   * exactamente que campos me falta llenar». La lista de abajo dice qué
   * falta; esto dice **dónde**, que en un formulario de ocho bloques no es
   * lo mismo: «falta la orden de compra» obliga a recorrerlo entero.
   */
  const marcar = (falta: boolean) => (falta
    ? { borderColor: '#c92a2a', background: '#fdf0ec' }
    : undefined);

  /** Y el porqué, debajo del campo. */
  const PorQue = ({ si, children }: { si: boolean; children: ReactNode }) => (
    si ? <div className="conteo" style={{ color: '#c92a2a' }}>{children}</div> : null
  );

  const impedimentos: string[] = [];
  if (!clienteId) impedimentos.push('Falta el cliente.');
  if (!poNumero.trim()) impedimentos.push('Falta la orden de compra del cliente.');
  if (sinDescripcion) impedimentos.push('Hay renglones sin descripción.');
  if (sinPrecio) impedimentos.push('Hay renglones en cero. El precio se captura antes de emitir.');
  if (marcasPendientes) impedimentos.push('Hay descripciones con marcas sin resolver.');
  if (tipo === 'iguala' && !periodoInicio) impedimentos.push('Falta el periodo que cubre la iguala.');

  /**
   * El cuerpo que se manda, armado una sola vez.
   *
   * Lo usan generar y la vista previa. Dos copias del mismo cuerpo se
   * separan al primer campo nuevo, y entonces lo que se revisa deja de ser
   * lo que se emite — que es justo lo que la vista previa viene a evitar.
   */
  function elCuerpo(): Record<string, unknown> {
    return {
        tipo,
        serie: serie || undefined,
        clienteId,
        sucursalId: sucursalId ?? undefined,
        ordenesTrabajoId: ordenTrabajoId ? [ordenTrabajoId] : undefined,

        // Lo que se dejó vacío se guarda con lo que diga la ficha del
        // cliente. Vaciar un campo aquí no lo borra de la factura: lo
        // devuelve a su valor de siempre, que es lo que se espera de un
        // campo prellenado.
        usoCfdi: oNada(fiscales.usoCfdi),
        formaPago: oNada(fiscales.formaPago),
        formaPagoTexto: oNada(fiscales.formaPagoTexto),
        metodoPago: oNada(fiscales.metodoPago),
        metodoPagoEdi: oNada(fiscales.metodoPagoEdi),
        condicionesPago: oNada(fiscales.condicionesPago),
        cuentaPago: oNada(fiscales.cuentaPago),
        moneda: MONEDA,
        diasCredito: fiscales.diasCredito.trim() === ''
          ? undefined : Number(fiscales.diasCredito),

        poNumero: poNumero.trim(),
        fechaOc: fechaOc || undefined,
        contraRecibo: oNada(contraRecibo),
        contraReciboFecha: contraReciboFecha || undefined,

        eanEntrega: eanEntrega.trim() || undefined,
        periodoInicio: periodoInicio || undefined,
        periodoFin: periodoFin || undefined,
        conceptos: renglones.map((r) => ({
          descripcion: r.descripcion.trim(),
          cantidad: Number(r.cantidad),
          precioUnitario: Number(r.precioUnitario),
          claveProdServ: r.claveProdServ,
          claveUnidad: r.claveUnidad,
          unidad: r.unidad,
          codigoUpc: r.codigoUpc || undefined,
          emiteCodigoUpc: r.emiteCodigoUpc,
          objetoImpuesto: r.objetoImpuesto,
          // Fijo, pero viaja renglón por renglón porque así lo pide el
          // archivo: las etiquetas TASIPE y MONIPE son de cada concepto.
          tasaIva: TASA_IVA,
        })),
    };
  }

  /**
   * El comprobante como va a quedar, sin generar nada.
   *
   * Karin: «antes de generar prefactura podemos hacer un preview?».
   *
   * Lo arma la API con el mismo código que después lo guarda, así que no
   * es una maqueta parecida: es el documento. Y de paso es un ensayo —todo
   * lo que el guardado rechaza sale aquí— antes de consumir un folio, que
   * es el punto de no retorno de verdad: sale de una serie fiscal y no se
   * devuelve.
   */
  async function previsualizar() {
    setPrevisualizando(true);
    setError(null);

    try {
      setPrevia(await api.post<VistaPreviaPrefactura>(
        '/facturacion/prefacturas/previsualizar', elCuerpo(),
      ));
    } catch (e) {
      setError(e);
    } finally {
      setPrevisualizando(false);
    }
  }

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      const creada = await api.post<Prefactura>(
        '/facturacion/prefacturas', elCuerpo(),
      );

      alCrear(creada);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  if (cargando || !emision) return <Cargando que="Preparando la prefactura…" />;

  const serieElegida = emision.series.find((s) => s.serie === serie);
  const divisa = MONEDA;

  return (
    <div className="panel">
      <div className="encabezado">
        <div>
          <h2>Crear prefactura</h2>
          <p className="sub">
            De aquí sale el archivo que Master EDI convierte en XML y PDF.
            Mientras el UUID no vuelva, esto es una prefactura: no es el
            comprobante fiscal y no se le manda al cliente.
          </p>
        </div>
        {serieElegida && (
          <div className="conteo">
            Le tocará el folio{' '}
            <strong className="mono">
              {serieElegida.serie}-{serieElegida.siguiente_folio}
            </strong>
          </div>
        )}
      </div>

      <Error error={error} />

      {borrador && borrador.faltantes.length > 0 && (
        <Faltantes que={borrador.faltantes} />
      )}

      {/* ═══════════════════════════════════════════════════ 1 · emisor */}
      <Bloque
        titulo="Emisor"
        origen="traido"
        nota="Siempre es APRISA. Se toma de la configuración de la empresa y no se captura."
      >
        <div className="rejilla-datos">
          <Dato etiqueta="Razón social" valor={emision.emisor.razonSocial} />
          <Dato etiqueta="RFC" valor={emision.emisor.rfc} mono />
          <Dato
            etiqueta="Régimen fiscal"
            valor={conNombre(sat?.regimenesFiscales, emision.emisor.regimenFiscal)}
          />
          <Dato etiqueta="Teléfono" valor={emision.emisor.telefono} />
          <div className="ancho-total">
            <Dato etiqueta="Domicilio fiscal" valor={domicilioDelEmisor(emision.emisor)} />
          </div>
        </div>
      </Bloque>

      {/* ══════════════════════════════════════════════════ 2 · cliente */}
      <Bloque titulo="Cliente" origen="traido">
        {borrador ? (
          <p className="sub" style={{ marginTop: 0 }}>
            Lo pone la orden <strong className="mono">{folioOT(borrador.orden.folio)}</strong>
          </p>
        ) : (
          <div className="campo" style={{ maxWidth: 420 }}>
            <label htmlFor="cliente">A quién se le factura</label>
            <select
              id="cliente" value={clienteId} style={marcar(!clienteId)}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">Elige…</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            <span className="ayuda">
              De él salen el RFC, el domicilio y las condiciones de pago.
              Se dan de alta en Clientes.
            </span>
          </div>
        )}

        {/* Lo que se va a copiar al comprobante, a la vista antes de
            crearlo. Escoger un cliente y no ver con qué datos se va a
            timbrar es enterarse del hueco cuando el TXT ya se generó, y
            para entonces ya consumió folio. */}
        {receptor && <DatosDelCliente cliente={receptor} regimenes={sat?.regimenesFiscales} />}
      </Bloque>

      {/* ═══════════════════════════════════════════════ 3 · comprobante */}
      {/* Con una orden detrás no hay nada que escoger: el tipo lo dice
          la orden y el resto está pactado. En la factura libre sí. */}
      <Bloque titulo="Comprobante" origen={deLaOrden ? 'traido' : 'completar'}>
        <div className="rejilla-campos">
          <div className="campo">
            <label htmlFor="tipo">Tipo</label>
            <select
              id="tipo" value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoFactura)}
              disabled={!!borrador}
            >
              {/* «Iguala» ya no se ofrece: era el de la correctiva sin
                  costo, y ésa dejó de facturarse. Se sigue enseñando en
                  una factura vieja que lo tenga —la opción aparece sólo
                  entonces— para que el desplegable no salga en blanco
                  sobre un valor que existe. */}
              {tipo === 'iguala' && <option value="iguala">Iguala (retirado)</option>}
              <option value="preventivo">Preventivo</option>
              <option value="correctivo">Correctivo</option>
              <option value="libre">Libre</option>
            </select>
            <span className="ayuda">
              {tipo === 'iguala'
                ? 'Retirado: era el de la correctiva sin costo, que ya no se factura.'
                : tipo === 'preventivo'
                  ? 'La visita del programa preventivo. Se cobra siempre, sea cual'
                    + ' sea la iguala de la sucursal.'
                  : tipo === 'correctivo'
                    ? 'El trabajo que se cobra aparte de la iguala.'
                    : 'Sin orden detrás: facturación captura todo.'}
            </span>
          </div>

          {/* La serie de una factura de Walmart es la E, siempre. El
              desplegable se queda para la factura libre, donde sí hay
              que escoger. */}
          <div className="campo">
            <label htmlFor="serie">Serie</label>
            {deLaOrden ? (
              <Fijo valor={serie} ayuda="El folio lo asigna el sistema al generar." />
            ) : (
              <>
                {/* Sólo las activas. Karin: «poner por default la serie
                    E y quitar los historicos». Las retiradas estaban en
                    la lista deshabilitadas, que es lo mismo que no
                    poderlas elegir pero con cuatro renglones de ruido
                    encima del que sí se usa.

                    Y no se pierden: lo ya emitido con ellas sigue
                    diciendo su serie, y la lista de series se administra
                    desde Administración. Lo que no se puede es emitir una
                    nueva con una serie retirada, que es exactamente lo
                    que significa retirarla. */}
                <select id="serie" value={serie} onChange={(e) => setSerie(e.target.value)}>
                  {emision.series.filter((s) => s.activa).map((s) => (
                    <option key={s.id} value={s.serie}>{s.serie}</option>
                  ))}
                </select>
                <span className="ayuda">El folio lo asigna el sistema al generar.</span>
              </>
            )}
          </div>

          <div className="campo">
            <label>Efecto de comprobante</label>
            <span className="mono">I · Ingreso</span>
            <span className="ayuda">
              APRISA sólo emite de ingreso. El egreso es la nota de
              crédito, que todavía no se hace desde aquí.
            </span>
          </div>

          {deLaOrden ? (
            <div className="campo">
              <label>Uso de CFDI</label>
              <Fijo valor={conNombre(sat?.usosCfdi, fiscales.usoCfdi) ?? fiscales.usoCfdi} />
            </div>
          ) : (
            <SelectSat
              id="usoCfdi" etiqueta="Uso de CFDI" opciones={sat?.usosCfdi ?? []}
              valor={fiscales.usoCfdi}
              alCambiar={(v) => setFiscales({ ...fiscales, usoCfdi: v })}
              origen={receptor?.usoCfdi ?? null}
            />
          )}

          <div className="campo">
            <label>Moneda</label>
            <span className="mono">MXN · Peso mexicano</span>
            <span className="ayuda">
              APRISA factura en pesos. Facturar en otra moneda pide
              también el tipo de cambio, que hoy va fijo.
            </span>
          </div>

          <div className="campo">
            <label>Tipo de cambio</label>
            <span className="mono">1</span>
            <span className="ayuda">En pesos siempre es 1.</span>
          </div>
        </div>
      </Bloque>

      {/* ═════════════════════════════════════ 4 · condiciones de pago */}
      {receptor && (
        <Bloque
          titulo="Condiciones de pago"
          origen={deLaOrden ? 'traido' : 'completar'}
          nota={deLaOrden
            ? 'Con Walmart están pactadas y no cambian de una factura a otra.'
              + ' Lo único que se toca son los días de crédito, que sí se'
              + ' renegocian.'
            : 'Se proponen las del cliente. Cambiarlas aquí cambia esta factura'
              + ' y nada más; para cambiarlas siempre está el catálogo de Clientes.'}
        >
          <div className="rejilla-campos">
            {deLaOrden ? (
              <>
                <div className="campo">
                  <label>Método de pago</label>
                  <Fijo
                    valor={conNombre(sat?.metodosDePago, fiscales.metodoPago)
                      ?? fiscales.metodoPago}
                  />
                </div>

                <div className="campo">
                  <label>Forma de pago</label>
                  <Fijo
                    valor={conNombre(sat?.formasDePago, fiscales.formaPago)
                      ?? fiscales.formaPago}
                  />
                </div>

                <div className="campo">
                  <label>Condiciones de pago</label>
                  <Fijo valor={fiscales.condicionesPago} />
                </div>

                {/* El METPAG del archivo. No es del catálogo del SAT: cada
                    cliente lo pide con sus propias palabras, y Walmart lo
                    pide así. */}
                <div className="campo">
                  <label>Método de pago del cliente</label>
                  <Fijo valor={fiscales.metodoPagoEdi} ayuda="Es el METPAG del archivo." />
                </div>
              </>
            ) : (
              <>
                <SelectSat
                  id="metodoPago" etiqueta="Método de pago" opciones={sat?.metodosDePago ?? []}
                  valor={fiscales.metodoPago}
                  alCambiar={(v) => setFiscales({ ...fiscales, metodoPago: v })}
                  origen={receptor.metodoPago}
                />

                <SelectSat
                  id="formaPago" etiqueta="Forma de pago" opciones={sat?.formasDePago ?? []}
                  valor={fiscales.formaPago}
                  alCambiar={(v) => {
                    // El archivo pide la forma de pago dos veces, con clave y
                    // con letra, y tienen que decir lo mismo. Se escribe sola
                    // al escoger para que no puedan discrepar.
                    const nombre = (sat?.formasDePago ?? [])
                      .find((f) => f.clave === v)?.nombre ?? '';
                    setFiscales({ ...fiscales, formaPago: v, formaPagoTexto: nombre });
                  }}
                  origen={receptor.formaPago}
                />

                <div className="campo">
                  <label htmlFor="formaPagoTexto">Forma de pago, escrita</label>
                  <input
                    id="formaPagoTexto" value={fiscales.formaPagoTexto} maxLength={60}
                    onChange={(e) => setFiscales({ ...fiscales, formaPagoTexto: e.target.value })}
                  />
                  <span className="ayuda">
                    El archivo la pide también con letra. Se llena sola al
                    escoger la clave.
                  </span>
                </div>

                <div className="campo">
                  <label htmlFor="condicionesPago">Condiciones de pago</label>
                  <input
                    id="condicionesPago" value={fiscales.condicionesPago} maxLength={150}
                    onChange={(e) => setFiscales({ ...fiscales, condicionesPago: e.target.value })}
                    placeholder="CREDITO"
                  />
                </div>

                <div className="campo">
                  <label htmlFor="metodoPagoEdi">Método de pago del cliente</label>
                  <input
                    id="metodoPagoEdi" value={fiscales.metodoPagoEdi} maxLength={60}
                    onChange={(e) => setFiscales({ ...fiscales, metodoPagoEdi: e.target.value })}
                  />
                  <span className="ayuda">
                    No es del catálogo del SAT: cada cliente lo pide con sus
                    propias palabras.
                  </span>
                </div>
              </>
            )}

            {/* Los días sí se capturan, también con orden detrás: Karin
                pidió 90 y las facturas de agosto dicen 30, así que el
                plazo se renegocia y la pantalla tiene que dejarlo. */}
            <div className="campo">
              <label htmlFor="diasCredito">Días de crédito</label>
              <input
                id="diasCredito" type="number" min="0" step="1"
                value={fiscales.diasCredito}
                onChange={(e) => setFiscales({ ...fiscales, diasCredito: e.target.value })}
              />
              <span className="ayuda">
                De aquí sale el vencimiento: se cuentan desde la expedición.
              </span>
            </div>
          </div>
        </Bloque>
      )}

      {/* ═══════════════════════════════ 5 · referencias del cliente */}
      {/*
          El bloque «Lugar de entrega» desapareció y su EAN vive aquí.

          Karin: «módulo lugar de entrega, quitar, pero el EAN de la
          tienda lo acomodamos en otro módulo porque creo que lo necesita
          para generar el txt». Lo necesita —es la etiqueta EANENT— y
          además es una referencia del cliente como las demás: dice a qué
          tienda se le hizo el trabajo. Un bloque entero para un campo era
          lo que no tenía sentido.
      */}
      {/* El único bloque que de verdad pide algo en una factura de
          Walmart, y lo único que pide es el número de contra-recibo,
          cuando el cliente lo da. */}
      <Bloque
        titulo="Referencias del cliente"
        origen="completar"
        nota="Los números y las fechas con los que el cliente casa la factura de su lado."
      >
        <div className="rejilla-campos">
          <div className="campo">
            <label htmlFor="po">Orden de trabajo</label>
            <input
              id="po" value={poNumero} className="mono"
              style={marcar(!poNumero.trim())}
              onChange={(e) => setPoNumero(e.target.value)}
              placeholder="El número que asigna el cliente"
            />
            <span className="ayuda">
              {deLaOrden
                ? 'Es el folio de la OT: el número con el que el cliente pidió el trabajo.'
                : 'Sin orden detrás se captura a mano.'}
            </span>

            {/* Avisa y no impide, con los folios a la mano: un «ya existe»
                a secas obliga a ir a buscarlas a otra pantalla, que es
                justo el paso que nadie da. Las canceladas se marcan,
                porque una cancelada explica por qué el número se repite y
                esconderla haría que el aviso pareciera un error. */}
            {yaFacturada.length > 0 && (
              <div className="aviso" style={{ marginTop: 6 }}>
                <strong>
                  Esa orden ya se facturó
                  {yaFacturada.length > 1 ? ` ${yaFacturada.length} veces` : ''}.
                </strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {yaFacturada.map((f) => (
                    <li key={f.id}>
                      <span className="mono">{f.folio}</span>
                      {' · '}{dia(f.fecha)}
                      {' · '}{cifra(f.total)}
                      {f.cancelada && <span className="etiqueta no" style={{ marginLeft: 6 }}>cancelada</span>}
                      {!f.cancelada && !f.timbrada && (
                        <span className="etiqueta" style={{ marginLeft: 6 }}>sin timbrar</span>
                      )}
                    </li>
                  ))}
                </ul>
                <p style={{ margin: '6px 0 0' }}>
                  Si es una refacturación o un trabajo partido en dos entregas,
                  sigue adelante. Si no, revisa el número antes de generar.
                </p>
              </div>
            )}
          </div>

          {/* La OS con la que se cerró la orden.

              Karin: «falta agregar la orden de servicio con que fue
              cerrada la orden». Es el papel que el técnico llevó firmado
              de la tienda, y es por donde se sostiene el cierre: cuando
              el cliente pregunta de dónde sale un cobro, ése es el
              documento que se enseña.

              Sólo se ve cuando la factura viene de una orden, porque sale
              del cierre y no de quien factura. Una libre no tiene OS que
              enseñar, y un campo vacío invitaría a inventarle una.

              No viaja al archivo de timbrado: el formato de Master EDI no
              tiene dónde ponerla —se revisaron las tres facturas
              timbradas y ninguna la lleva—. Vive aquí para quien factura
              y para quien después pregunte. */}
          {deLaOrden && (
            <div className="campo">
              <label>Orden de servicio</label>
              <Fijo
                valor={borrador?.orden?.folioServicio || '—'}
                ayuda="La OS firmada con la que se cerró la orden."
              />
            </div>
          )}

          {/* La fecha de la orden sale de la OT y no se teclea.

              Karin: «fecha de orden (toma la fecha que se dio de alta la
              orden en sistema)». Tecleada, es cuestión de tiempo que una
              factura diga una fecha y su orden otra.

              Se guarda, pero el formato impreso ya no la enseña: el
              archivo de Master EDI no tiene campo para ella, así que la
              factura del cliente sale sin esa fecha. */}
          <div className="campo">
            <label htmlFor="fechaOc">Fecha de la orden</label>
            {deLaOrden ? (
              <Fijo
                valor={fechaOc || '—'}
                ayuda="El día que la OT entró al sistema. No sale impresa en la factura."
              />
            ) : (
              <input
                id="fechaOc" type="date" value={fechaOc}
                onChange={(e) => setFechaOc(e.target.value)}
              />
            )}
          </div>

          {/* Arranca hoy, venga de una orden o no, y se puede cambiar.

              Karin: «la fecha a registrar sería "Hoy", pero aparece la
              fecha en la que se cerró la orden». Antes la que venía de una
              orden tomaba su cierre y ni siquiera era editable, así que
              una orden cerrada el 5 y facturada el 13 salía con un
              contra-recibo fechado ocho días antes de existir, sin manera
              de corregirlo.

              El contra-recibo se fecha el día en que se le entrega la
              factura al cliente. Editable porque a veces se entrega otro
              día —se mandó el viernes y lo acusaron el lunes— y eso sólo
              lo sabe quien la entregó. */}
          <div className="campo">
            <label htmlFor="contraReciboFecha">Fecha del contra-recibo</label>
            <input
              id="contraReciboFecha" type="date" value={contraReciboFecha}
              onChange={(e) => setContraReciboFecha(e.target.value)}
            />
            <span className="ayuda">
              Hoy por omisión: es el día en que se entrega la factura, no el
              del cierre de la orden. Se puede cambiar.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="contraRecibo">Contra-recibo</label>
            <input
              id="contraRecibo" value={contraRecibo} className="mono" maxLength={50}
              onChange={(e) => setContraRecibo(e.target.value)}
            />
            <span className="ayuda">
              El número, cuando el cliente lo da. En las de Walmart va vacío.
            </span>
          </div>

          <div className="campo">
            <label>Número de proveedor</label>
            <Fijo
              valor={receptor?.numeroProveedor || '—'}
              ayuda="Con el que este cliente tiene registrada a APRISA. Sale de su ficha."
            />
          </div>

          {/* La tienda y su EAN.

              Estuvieron un rato en el bloque de Cliente y vuelven aquí a
              petición de Karin. Tiene sentido: este bloque son las
              referencias con las que el cliente casa la factura de su
              lado, y la tienda es una de ellas — su reporte de pagos trae
              una columna «Tienda» justo para eso.

              La sucursal se queda como campo y no como el subtítulo que
              era antes: se lee de un vistazo cuando se compara contra el
              PDF, que es para lo que sirve. */}
          {borrador?.sucursal && (
            <div className="campo">
              <label>Sucursal</label>
              <Fijo
                valor={`${borrador.sucursal.determinante} — ${borrador.sucursal.nombre}`}
                ayuda="La tienda donde se hizo el trabajo. La pone la orden."
              />
            </div>
          )}

          <div className="campo">
            <label htmlFor="ean">EAN de la tienda</label>
            <input
              id="ean" value={eanEntrega} className="mono"
              onChange={(e) => setEanEntrega(e.target.value)}
            />
            <span className="ayuda">
              El determinante de la sucursal. Viene de la orden y es lo que
              el archivo escribe como lugar de entrega.
            </span>
          </div>

          {tipo === 'iguala' && (
            <>
              <div className="campo">
                <label htmlFor="desde">Periodo desde</label>
                <input
                  id="desde" type="date" value={periodoInicio}
                  style={marcar(tipo === 'iguala' && !periodoInicio)}
                  onChange={(e) => setPeriodoInicio(e.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="hasta">Periodo hasta</label>
                <input
                  id="hasta" type="date" value={periodoFin}
                  onChange={(e) => setPeriodoFin(e.target.value)}
                />
                <span className="ayuda">
                  Lo que separa una iguala de otra es el periodo: mensual,
                  bimestral o trimestral.
                </span>
              </div>
            </>
          )}
        </div>
      </Bloque>

      {/* De dónde salió el precio. Un importe que aparece solo en el
          renglón se ve igual que uno tecleado; decir de qué tarifa viene
          es lo que permite dudar de él cuando no cuadra. */}
      {borrador?.tarifa && (
        <div className="aviso" style={{ marginBottom: 14 }}>
          El precio sale de la tarifa pactada para{' '}
          <strong>{borrador.tarifa.formato ?? 'este formato'}</strong>:{' '}
          <strong>{moneda(borrador.tarifa.precio, divisa)}</strong> {borrador.tarifa.periodicidad},
          vigente desde el {new Date(borrador.tarifa.vigenteDesde).toLocaleDateString('es-MX')}.
          Se puede cambiar aquí; cambiarla para siempre es en
          Administración → Tarifas de iguala.
        </div>
      )}

      {/* Lo que se hizo en la visita preventiva.
          Mismo criterio que con la cotización: la descripción del
          comprobante la manda el catálogo, así que el desglose no se
          vuelca a los renglones. Está aquí para que el importe de la
          factura se pueda contrastar con lo que se hizo, sin ir a
          buscarlo al programa. */}
      {borrador?.preventivo && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <h3 style={{ marginTop: 0 }}>Lo que se hizo en la visita</h3>
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Servicio</th>
                <th className="numero">Precio</th>
              </tr>
            </thead>
            <tbody>
              {borrador.preventivo.servicios.map((s, i) => (
                <tr key={i}>
                  <td className="mono">{s.codigo}</td>
                  <td>
                    {s.nombre}
                    {/* Los equipos que se atendieron. No cambian el
                        importe —el precio es del renglón y no se
                        multiplica— pero son lo que sostiene el renglón
                        si el cliente lo discute. */}
                    {s.equipos.length > 0 && (
                      <div className="ayuda" style={{ marginTop: 2 }}>
                        Equipos: {s.equipos.join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="numero">
                    {s.precio !== null
                      ? moneda(s.precio, divisa)
                      : <span className="etiqueta no">sin precio</span>}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={2}><strong>Total</strong></td>
                <td className="numero">
                  <strong>
                    {borrador.preventivo.total !== null
                      ? moneda(borrador.preventivo.total, divisa)
                      : 'no se puede sumar'}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>

          {borrador.preventivo.total === null && (
            <div className="aviso" style={{ marginTop: 10 }}>
              Algún servicio de esta visita no tenía precio capturado el día
              que se hizo, así que no hay importe que proponer. Se captura en
              Administración → Precios del preventivo, y para que esta orden
              lo tome hay que volver a marcar la visita como realizada.
            </div>
          )}
        </div>
      )}

      {/* Lo que cotizó el técnico y el cliente autorizó.
          Las descripciones de la factura son las del catálogo —el cliente
          exige esas leyendas—, así que el desglose no se vuelca a los
          renglones. Pero llenarlos sin tenerlo enfrente obliga a abrir la
          orden en otra pestaña, y ahí es donde se pierde un importe. */}
      {borrador && borrador.cotizacion.length > 0 && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <h3 style={{ marginTop: 0 }}>Lo que se cotizó en la orden</h3>
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Descripción</th>
                <th className="numero">Precio venta</th>
              </tr>
            </thead>
            <tbody>
              {borrador.cotizacion.map((r, i) => (
                <tr key={i}>
                  <td className="mono">{r.codigo ?? '—'}</td>
                  <td>{r.descripcion}</td>
                  <td className="numero">{moneda(r.precioVenta, divisa)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={2}><strong>Total cotizado</strong></td>
                <td className="numero"><strong>{moneda(totalCotizado, divisa)}</strong></td>
              </tr>
            </tbody>
          </table>

          {difiere && (
            <div className="aviso" style={{ marginTop: 10 }}>
              El subtotal de la prefactura es{' '}
              <strong>{moneda(totales.subtotal, divisa)}</strong> y
              lo cotizado fue <strong>{moneda(totalCotizado, divisa)}</strong>. Puede ser
              correcto —el cliente pudo autorizar otra cosa—, pero conviene
              mirarlo antes de generar: es la diferencia entre lo que se
              autorizó y lo que se va a cobrar.
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════ 7 · conceptos */}
      {/* ══════════════════════════════════════════════ 7 · conceptos

          Desde la 0063 los renglones son los de la orden. Karin, con la
          respuesta del cliente: «todos los servicios que ponen en las OT
          desde que soporte las captura deben coincidir hasta el final pues
          esos son los conceptos que se facturan […] en realidad no
          agregamos nada adicional a esa factura solo arrastramos lo que
          trae la orden».

          Así que cuando vienen de una OT no hay nada que capturar: se
          leen. Bloquear los campos no es lo que lo garantiza —la API
          rechaza unos conceptos que no sean los de la orden— pero sí es lo
          que evita que alguien intente y descubra al guardar que no. */}
      <Bloque
        titulo="Conceptos"
        origen={vienenDeLaOrden ? 'traido' : 'completar'}
        nota={vienenDeLaOrden
          ? 'Son los renglones que se cotizaron en la orden, con el precio que'
            + ' autorizó Operaciones. No se editan ni se agregan: si algo está'
            + ' mal, se corrige en la orden y la factura lo vuelve a tomar.'
            + ` Todos llevan IVA al ${TASA_IVA}%.`
          : `Todos los renglones llevan IVA al ${TASA_IVA}%. La clave de unidad`
            + ' no se escoge: lo único que cambia de un renglón a otro es si'
            + ' se imprime como pieza o como servicio.'}
      >
        {/* Sin un solo renglón, decirlo aquí y no al guardar.
            Karin: «no me deja generar la vista me dice que al menos un
            concepto». El mensaje del guardado no dice ni qué falta ni
            dónde arreglarlo; la lista de arriba sí, y esto manda a
            leerla. */}
        {renglones.length === 0 && (
          <div className="aviso error">
            <strong>Esta factura no tiene ningún concepto todavía.</strong>{' '}
            {vienenDeLaOrden
              ? 'Los renglones salen de la cotización de la orden, y no salió'
                + ' ninguno. Arriba está el porqué —un renglón sin precio de'
                + ' venta, o el catálogo sin sus plantillas marcadas— y dónde'
                + ' se arregla.'
              : 'Agrega al menos uno con «Renglón en blanco».'}
          </div>
        )}

        {vienenDeLaOrden ? (
          <table>
            <EncabezadoDeConceptos />
            <tbody>
              {renglones.map((r) => (
                <tr key={r.llave}>
                  <td>{r.descripcion}</td>
                  <td className="numero">{Math.round(Number(r.cantidad) || 0)}</td>
                  <td className="mono">{r.claveProdServ}</td>
                  <td className="mono">{UNIDAD_SAT}</td>
                  <td>{r.unidad === 'SERVICIO' ? 'Servicio' : 'Pieza'}</td>
                  <td className="numero">{moneda(Number(r.precioUnitario), divisa)}</td>
                  <td className="numero">
                    {moneda(
                      importeDe(Number(r.cantidad) || 0, Number(r.precioUnitario) || 0),
                      divisa,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
        <>
        <table>
          <EncabezadoDeConceptos conAcciones />
          <tbody>
            {renglones.map((r) => (
              <tr key={r.llave}>
                <td>
                  <input
                    style={{ width: '100%', ...marcar(!r.descripcion.trim()) }}
                    value={r.descripcion}
                    onChange={(e) => cambiar(r.llave, 'descripcion', e.target.value)}
                  />
                  <PorQue si={!r.descripcion.trim()}>
                    Falta la descripción
                  </PorQue>
                  {/* Una marca sin resolver se ve antes de guardar; la API
                      la rechaza de todos modos, pero enterarse aquí cuesta
                      menos que enterarse al guardar. */}
                  {/\{(sucursal|determinante)\}/.test(r.descripcion) && (
                    <div className="conteo" style={{ color: '#b02a37' }}>
                      Falta resolver la marca
                    </div>
                  )}
                </td>
                {/* Enteros. Karin: «cantidad solo números enteros». Lo que
                    se factura son piezas y servicios, y no hay medio
                    servicio; el paso decimal sólo servía para colar un
                    2.5 que después nadie explica. */}
                <td className="numero">
                  <input
                    type="number" min="1" step="1" style={{ width: 70 }}
                    value={r.cantidad}
                    onChange={(e) => cambiar(
                      r.llave, 'cantidad', Math.max(1, Math.round(Number(e.target.value) || 1)),
                    )}
                  />
                </td>
                <td>
                  <ClaveConOtra
                    valor={r.claveProdServ ?? ''}
                    opciones={(emision.enUso.prodServ).map((p) => ({
                      clave: p.clave, nombre: p.clave,
                    }))}
                    alCambiar={(v) => cambiar(r.llave, 'claveProdServ', v)}
                    ancho={130}
                  />
                </td>
                {/* La clave de unidad es E48 en los tres tipos de factura,
                    y lo único que cambia es cómo se imprime: PZA o
                    SERVICIO. Karin: «clave unidad es E48 fijo, unidad es
                    pieza o servicio».

                    Era un desplegable de todo el catálogo del SAT más un
                    campo de texto libre, o sea dos maneras de sacar un
                    renglón que dice una clave y otra palabra. */}
                {/* La clave no se escoge: es E48 en los tres tipos de
                    factura. Va en su propia columna porque es un dato del
                    renglón y el papel también la imprime aparte. */}
                <td className="mono">{UNIDAD_SAT}</td>
                <td>
                  <select
                    style={{ width: '100%' }}
                    value={r.unidad === 'SERVICIO' ? 'SERVICIO' : 'PZA'}
                    onChange={(e) => setRenglones((rs) => rs.map((x) => (x.llave === r.llave
                      ? { ...x, claveUnidad: UNIDAD_SAT, unidad: e.target.value }
                      : x)))}
                  >
                    <option value="PZA">Pieza</option>
                    <option value="SERVICIO">Servicio</option>
                  </select>
                </td>
                <td className="numero">
                  <input
                    type="number" min="0" step="0.01"
                    style={{ width: 110, ...marcar(!(Number(r.precioUnitario) > 0)) }}
                    value={r.precioUnitario}
                    onChange={(e) => cambiar(r.llave, 'precioUnitario', Number(e.target.value))}
                  />
                  <PorQue si={!(Number(r.precioUnitario) > 0)}>
                    Falta el precio
                  </PorQue>
                </td>
                <td className="numero">
                  {moneda(
                    importeDe(Number(r.cantidad) || 0, Number(r.precioUnitario) || 0),
                    divisa,
                  )}
                </td>
                <td>
                  <button
                    onClick={() => setRenglones((rs) => rs.filter((x) => x.llave !== r.llave))}
                    disabled={renglones.length === 1}
                  >
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Sólo el renglón en blanco.

            Karin: «ocultar el select de agregar del catálogo, no lo vamos
            a ocupar por el momento». El catálogo de conceptos todavía no
            tiene pantalla donde mantenerse —es la tarea #193— así que un
            desplegable que ofrece lo que nadie ha podido capturar sale
            vacío o con datos de prueba, y eso es peor que no estar: quien
            lo abre creyendo que ahí está lo suyo pierde el tiempo y
            desconfía del resto.

            Se retiró de la pantalla y no se dejó escondido detrás de una
            condición: código que nadie ejecuta envejece sin que nadie lo
            note, y el día que se vuelva a encender ya no sirve. El
            endpoint `/facturacion/prefacturas/conceptos` sigue ahí y el
            armado del renglón está en el historial, que es donde se
            recupera cuando haya qué ofrecer. */}
        <div className="controles" style={{ marginTop: 12 }}>
          <button onClick={() => setRenglones((rs) => [...rs, nuevoRenglon()])}>
            Renglón en blanco
          </button>
        </div>
        </>
        )}
      </Bloque>

      {/* ════════════════════════════════════════════════ 8 · totales */}
      <div style={{ marginTop: 18, textAlign: 'right', display: 'grid', gap: 4 }}>
        <div>Subtotal <strong>{moneda(totales.subtotal, divisa)}</strong></div>
        <div>IVA <strong>{moneda(totales.iva, divisa)}</strong></div>
        <div style={{ fontSize: 18 }}>Total <strong>{moneda(totales.total, divisa)}</strong></div>
      </div>

      {impedimentos.length > 0 && <Faltantes que={impedimentos} />}

      <div className="controles" style={{ marginTop: 16 }}>
        {/* Revisar va antes y se ve primero: generar toma un folio de una
            serie fiscal y no se devuelve. Karin: «antes de generar
            prefactura podemos hacer un preview?». */}
        <button
          className="primario"
          onClick={() => { void previsualizar(); }}
          disabled={previsualizando || guardando || impedimentos.length > 0}
        >
          {previsualizando ? 'Armando…' : 'Ver cómo va a quedar'}
        </button>
        <button onClick={guardar} disabled={guardando || impedimentos.length > 0}>
          {guardando ? 'Generando…' : 'Generar prefactura'}
        </button>
        {alCancelar && (
          <button onClick={alCancelar} disabled={guardando}>
            Cancelar
          </button>
        )}
      </div>

      {/* El comprobante como va a quedar.

          Sale del mismo armado que después lo guarda, así que lo que se
          revisa aquí es lo que se emite. Se cierra o se genera desde el
          mismo sitio: mandar a revisar y después obligar a buscar otra vez
          el botón es como se genera sin haber leído. */}
      {previa && (
        <Dialogo
          titulo="Así va a quedar"
          ancho
          onCerrar={() => setPrevia(null)}
          pie={(
            <>
              <button onClick={() => setPrevia(null)}>Seguir editando</button>
              <button
                className="primario"
                onClick={() => { setPrevia(null); void guardar(); }}
                disabled={guardando}
              >
                {guardando ? 'Generando…' : 'Generar prefactura'}
              </button>
            </>
          )}
        >
          <ComprobanteDeLaFactura previa={previa} divisa={divisa} sat={sat} momento="borrador" />
        </Dialogo>
      )}
    </div>
  );
}

/**
 * Un bloque del formulario, con una franja que dice de dónde sale.
 *
 * Karin: «puedes usar colores para diferenciar las secciones que debe
 * completar la persona que factura, aunque la realidad es que casi todos
 * los datos ya vienen prellenados, sobre todo las que vienen de OTs».
 *
 * Eso es justo el problema que resuelve el color: un formulario largo con
 * todo del mismo tono obliga a leerlo entero para descubrir que casi nada
 * hay que tocar. Dos franjas y se ve de un vistazo.
 *
 *   traido    verde. Viene de la orden o del catálogo del cliente. No se
 *             captura, y en los conceptos ni siquiera se puede.
 *   completar ámbar. Lo poco que sí le toca a quien factura.
 */
function Bloque({ titulo, nota, origen, children }: {
  titulo: string;
  nota?: string;
  /**
   * Obligatorio a propósito, y sin valor por omisión.
   *
   * Lo tenía —«completar»— y por eso el bloque de Emisor, que no tiene un
   * solo campo, salía con la etiqueta ámbar diciendo que había algo que
   * llenar. Una etiqueta que miente es peor que ninguna: la primera vez
   * hace perder el tiempo buscando el campo, y a partir de la segunda ya
   * no se leen ninguna de las dos.
   *
   * Sin valor por omisión, un bloque nuevo no compila hasta que alguien
   * decida cuál de los dos es.
   */
  origen: 'traido' | 'completar';
  children: ReactNode;
}) {
  const traido = origen === 'traido';

  return (
    <section
      className="panel"
      style={{
        marginBottom: 14,
        borderLeft: `4px solid ${traido ? '#7a9a78' : '#c9a86a'}`,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
      }}>
        <h3 style={{ marginTop: 0, marginBottom: nota ? 4 : 12 }}>{titulo}</h3>
        <span
          className="etiqueta"
          style={{
            background: traido ? '#eef6ee' : '#fdf3e3',
            color: traido ? '#3f5c3e' : '#7a5c1e',
          }}
        >
          {traido ? 'Ya viene lleno' : 'Completar'}
        </span>
      </div>
      {nota && <p className="sub" style={{ marginTop: 0 }}>{nota}</p>}
      {children}
    </section>
  );
}

/**
 * Un desplegable de claves del SAT.
 *
 * Enseña la clave y su nombre juntos —«G03 · Gastos en general»— porque
 * la clave sola no dice nada y el nombre solo no es lo que viaja en el
 * archivo. Y admite un valor que no esté en la lista sin borrarlo: si un
 * cliente viejo tiene capturada una clave que el SAT retiró, la pantalla
 * la enseña en vez de cambiarla sola por otra.
 */
function SelectSat({ id, etiqueta, opciones, valor, alCambiar, origen }: {
  id: string;
  etiqueta: string;
  opciones: ClaveSat[];
  valor: string;
  alCambiar: (valor: string) => void;
  /** Lo que dice la ficha del cliente, para avisar cuando ya no coincide. */
  origen: string | null;
}) {
  const conocida = opciones.some((o) => o.clave === valor);
  const ayuda = opciones.find((o) => o.clave === valor)?.ayuda;
  const cambiado = origen !== null && origen !== '' && valor !== origen;

  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <select id={id} value={valor} onChange={(e) => alCambiar(e.target.value)}>
        <option value="">Elige…</option>
        {opciones.map((o) => (
          <option key={o.clave} value={o.clave}>{o.clave} · {o.nombre}</option>
        ))}
        {/* Una clave capturada que ya no está en el catálogo se conserva:
            perderla al abrir la pantalla sería cambiar el dato sin que
            nadie lo pidiera. */}
        {valor !== '' && !conocida && (
          <option value={valor}>{valor} · fuera del catálogo</option>
        )}
      </select>
      {ayuda && <span className="ayuda">{ayuda}</span>}
      {cambiado && (
        <span className="ayuda">
          El cliente tiene <strong className="mono">{origen}</strong>. Sólo cambia
          en esta factura.
        </span>
      )}
    </div>
  );
}

/**
 * Una clave escogida de una lista corta, con salida a teclear otra.
 *
 * c_ClaveProdServ tiene más de cincuenta mil valores y no cabe en un
 * desplegable. Las que APRISA usa son cinco o seis y salen del catálogo
 * de conceptos; para la excepción está «Otra…», que no obliga a
 * abandonar la pantalla.
 */
function ClaveConOtra({ valor, opciones, alCambiar, ancho }: {
  valor: string;
  opciones: { clave: string; nombre: string }[];
  alCambiar: (valor: string) => void;
  ancho: number;
}) {
  const conocida = opciones.some((o) => o.clave === valor);
  const [aMano, setAMano] = useState(false);

  if (aMano || (valor !== '' && !conocida)) {
    return (
      <input
        className="mono" style={{ width: ancho }} value={valor}
        onChange={(e) => alCambiar(e.target.value.trim())}
        onBlur={() => setAMano(false)}
        autoFocus={aMano}
      />
    );
  }

  return (
    <select
      className="mono" style={{ width: ancho }} value={valor}
      onChange={(e) => {
        if (e.target.value === '__otra__') { setAMano(true); return; }
        alCambiar(e.target.value);
      }}
    >
      <option value="">Elige…</option>
      {opciones.map((o) => (
        <option key={o.clave} value={o.clave}>{o.nombre}</option>
      ))}
      <option value="__otra__">Otra…</option>
    </select>
  );
}

/** El domicilio del emisor en un renglón, como se lee en la factura. */
function domicilioDelEmisor(e: DatosDeEmision['emisor']): string {
  return [
    [e.calle, e.numeroExterior].filter(Boolean).join(' '),
    e.numeroInterior ? `int. ${e.numeroInterior}` : '',
    e.colonia ? `Col. ${e.colonia}` : '',
    e.municipio,
    [e.codigoPostal ? `C.P. ${e.codigoPostal}` : '', e.estado].filter(Boolean).join(' '),
    e.pais,
  ].filter(Boolean).join(', ');
}


/**
 * Los datos fiscales del cliente, tal como van a copiarse.
 *
 * La prefactura no lee el catálogo al timbrar: copia estos valores al
 * crearse y no los vuelve a mirar. Verlos antes es la única oportunidad
 * de notar que falta el RFC —después el hueco aparece como una etiqueta
 * vacía en el TXT, que es donde no se ve—.
 */
function DatosDelCliente({ cliente, regimenes }: {
  cliente: DatosFiscalesDelCliente;
  regimenes: ClaveSat[] | undefined;
}) {
  const domicilio = [
    [cliente.direccionFacturacion, cliente.numeroExterior].filter(Boolean).join(' '),
    cliente.numeroInterior ? `int. ${cliente.numeroInterior}` : '',
    cliente.colonia ? `Col. ${cliente.colonia}` : '',
    [cliente.codigoPostal ? `C.P. ${cliente.codigoPostal}` : '', cliente.ciudad]
      .filter(Boolean).join(' '),
    cliente.estadoTexto,
  ].filter(Boolean).join(', ');

  // Sin estos, el timbrado se rechaza. Los demás huecos se notan pero no
  // detienen: un domicilio incompleto sale feo, un RFC ausente no sale.
  const faltan = [
    !cliente.razonSocial && 'la razón social',
    !cliente.rfc && 'el RFC',
    !cliente.regimenFiscal && 'el régimen fiscal',
    !domicilio && 'el domicilio fiscal',
  ].filter(Boolean) as string[];

  return (
    <div style={{ marginTop: 12 }}>
      <div className="rejilla-datos">
        <Dato etiqueta="Razón social" valor={cliente.razonSocial} />
        <Dato etiqueta="RFC" valor={cliente.rfc} mono />
        <Dato etiqueta="Régimen fiscal" valor={conNombre(regimenes, cliente.regimenFiscal)} />
        <div className="ancho-total">
          <Dato etiqueta="Domicilio fiscal" valor={domicilio || null} />
        </div>
      </div>

      {faltan.length > 0 && (
        <Faltantes que={[
          `Al cliente le falta ${faltan.join(', ')}. Se completa en Clientes,`
          + ' y hay que hacerlo antes de generar: la prefactura copia estos'
          + ' datos y no los vuelve a leer.',
        ]} />
      )}
    </div>
  );
}

/**
 * El encabezado de la tabla de conceptos, uno solo para las dos.
 *
 * Hay dos tablas de conceptos: la que enseña los renglones que vienen de
 * la orden —que no se editan— y la que los captura en una factura libre.
 * Tenían el encabezado copiado, y al ensanchar la descripción y quitar la
 * columna de IVA se me quedó una sin tocar: justo la que ve quien factura
 * una OT, que es el caso normal. Karin volvió a pedir el mismo cambio
 * porque para ella no había cambiado nada.
 *
 * Con un solo encabezado no pueden separarse. Los anchos van aquí y no en
 * la hoja de estilos porque son de esta tabla y de ninguna otra, y porque
 * el que importa —el de la descripción— es «lo que sobre».
 */
function EncabezadoDeConceptos({ conAcciones = false }: { conAcciones?: boolean }) {
  return (
    <thead>
      <tr>
        <th style={{ width: 'auto' }}>Descripción</th>
        <th className="numero" style={{ width: 90 }}>Cantidad</th>
        <th style={{ width: 130 }}>Clave SAT</th>
        {/* Dos columnas y no una.

            Karin: «tienes que poner 1 columna nueva que se llama clave
            unidad y ahí es donde poner E48; la columna unidad es la que
            muestra pieza o servicio». Son dos datos distintos —uno es la
            clave del SAT y el otro cómo se imprime— y el papel también
            los saca en columnas aparte: «Cve Uni» y «Unid.». */}
        <th style={{ width: 110 }}>Clave unidad</th>
        <th style={{ width: 130 }}>Unidad</th>
        <th className="numero" style={{ width: 120 }}>Valor unitario</th>
        <th className="numero" style={{ width: 110 }}>Importe</th>
        {conAcciones && <th style={{ width: 80 }} />}
      </tr>
    </thead>
  );
}

/**
 * Un valor que no se captura, en el sitio donde estaba su campo.
 *
 * No es un `Dato` con otro nombre: `Dato` es etiqueta y valor, y esto va
 * dentro de un `.campo` que ya trae su `<label>`. La diferencia importa
 * para que la rejilla no se descuadre al cambiar un desplegable por su
 * valor fijo.
 */
function Fijo({ valor, ayuda }: { valor: string; ayuda?: string }) {
  return (
    <>
      <span className="mono" style={{ padding: '6px 0', display: 'block' }}>{valor}</span>
      {ayuda && <span className="ayuda">{ayuda}</span>}
    </>
  );
}

function Dato({ etiqueta, valor, mono }: {
  etiqueta: string;
  valor: string | null;
  mono?: boolean;
}) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span className={mono ? 'mono' : undefined}>{valor || '—'}</span>
    </div>
  );
}
